import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchData } from "../../utils/functions";
import { useParams } from "react-router-dom";
import { ThinTextField } from "../common/GeneralComponent";

// Mapping between backend keys and frontend labels
const incomeSourceMap = {
  income_land_rent: "Land Rent",
  income_agricultural_produce: "Sale of Agricultural Produce",
  income_livestock_sale: "Sale of Livestock",
  income_livestock_products: "Sale of Livestock Products",
  income_business_profits: "Business Profits",
  income_remittance_abroad: "Remittance from abroad",
  income_remittance_domestic: "Remittance from within Country",
  income_salary_government: "Salary (Government Employment)",
  income_salary_private: "Salary (Private Employment)",
  income_daily_wages: "Daily Wages",
  income_social_transfers: "Social Transfers",
  income_scholarships: "Scholarships",
  income_other: "Others",
};

const EditHouseholdIncome = ({ householdIncome, DqaApproved, DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const householdId = householdIncome?.household_income_id || "";
  const { id } = useParams();
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [incomeSources, setIncomeSources] = useState({});
  const [otherSource, setOtherSource] = useState("");
  const [primarySource, setPrimarySource] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved || false);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected || false);
  const [householdInfoData, setHouseholdInfoData] = useState(null);
  const [errors, setErrors] = useState({}); // { monthlyIncome, incomeSources, otherSource }

  // Reverse mapping for easier lookup (not used below but kept if needed later)
  const labelToKeyMap = Object.entries(incomeSourceMap).reduce(
    (acc, [key, label]) => {
      acc[label] = key;
      return acc;
    },
    {}
  );

   useEffect(() => {
     setIsDqaApproved(DqaApproved || false);
     setIsDqaRejected(DqaRejected || false);
   }, [DqaApproved, DqaRejected]);

  useEffect(() => {
    if (householdIncome) {
      setMonthlyIncome(householdIncome.total_monthly_income || "");

      const sources = {};
      Object.keys(incomeSourceMap).forEach((key) => {
        if (householdIncome[key]) {
          const label = incomeSourceMap[key];
          sources[label] =
            key === "income_other"
              ? householdIncome.income_other_specify || true
              : true;
        }
      });

      setIncomeSources(sources);
      if (sources["Others"] && typeof sources["Others"] === "string") {
        setOtherSource(sources["Others"]);
      }

      setPrimarySource(householdIncome.primary_source_of_income || "");
    }
  }, [householdIncome]);

  const clearError = (name) =>
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });

  const handleCheckboxChange = (label, value) => {
    // value can be boolean (from checkbox) or string (from text input)
    const checked =
      typeof value === "string" ? value.trim().length > 0 : !!value;

    setIncomeSources((prev) => ({
      ...prev,
      [label]: checked,
    }));

    // Clear errors when user interacts
    setErrors((prev) => {
      const next = { ...prev };
      delete next.incomeSources;
      if (label === "Others") delete next.otherSource;
      return next;
    });

    // If unchecking Others, clear the text too
    if (label === "Others" && !checked) {
      setOtherSource("");
    }
  };

  const validate = () => {
    const e = {};

    // monthly income: required and non-negative number
    if (
      monthlyIncome === "" ||
      monthlyIncome === null ||
      monthlyIncome === undefined
    ) {
      e.monthlyIncome = "Total monthly income is required.";
    } else if (isNaN(Number(monthlyIncome)) || Number(monthlyIncome) < 0) {
      e.monthlyIncome = "Enter a non-negative number.";
    }

    // at least one source selected
    const anySource = Object.values(incomeSources).some(Boolean);
    if (!anySource) {
      e.incomeSources = "Select at least one income source.";
    }

    // if Others selected, require specify text
    if (incomeSources["Others"]) {
      if (!otherSource || otherSource.trim().length === 0) {
        e.otherSource = "Please specify the other income source.";
      } else if (otherSource.trim().length < 3) {
        e.otherSource = "Please enter at least 3 characters.";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!id) {
      toast.error("Household ID is missing");
      return;
    }

    // Block submit if invalid
    if (!validate()) {
      return;
    }

    setLoading(true);

    // Prepare backend payload
    const payload = {
      total_monthly_income: monthlyIncome,
      primary_source_of_income: primarySource,
      household_income_id: householdId,
      houshold_info_id: id,
    };

    // Convert label-based state to backend boolean fields
    Object.entries(incomeSourceMap).forEach(([key, label]) => {
      if (label === "Others" && incomeSources[label]) {
        payload[key] = true;
        payload["income_other_specify"] = otherSource;
      } else {
        payload[key] = !!incomeSources[label];
      }
    });

    try {
      const response = await fetchData(
        `household-income-update/${id}`,
        "PUT",
        payload,
        {},
        {},
        navigate,
        "/household",
        dispatch
      );

      if (response?.success) {
        toast.success("Household income updated successfully!");
        // Optional: clear errors after success
        setErrors({});
      } else {
        toast.error(response?.message || "Failed to update household income");
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update household income.");
    } finally {
      setLoading(false);
    }
  };

  // Helper to render error text
  const Err = (name) =>
    errors?.[name] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">{errors[name]}</p>
    ) : null;



  return (
    <div className="bg-white rounded">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Income
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Income
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      {householdIncome ? (
        <form onSubmit={handleSubmit} className="w-full">
          <div className="mb-6 grid grid-cols-3 gap-6 items-end">
            <ThinTextField
              isRequired={false}
              type="number"
              value={monthlyIncome}
              onChange={(e) => {
                setMonthlyIncome(e.target.value);
                clearError("monthlyIncome");
              }}
              label="What is the total Household monthly income? (Rs.)"
              placeholder="Enter total monthly income"
              disabled={isDqaApproved || isDqaRejected}
              error={!!errors.monthlyIncome}
              helperText={errors.monthlyIncome}
            />

            <ThinTextField
              isRequired={false}
              type="text"
              value={primarySource}
              onChange={(e) => {
                setPrimarySource(e.target.value);
                clearError("primarySource");
              }}
              label="What is primary source of income?"
              placeholder="Enter primary source of income"
              disabled={isDqaApproved || isDqaRejected}
              error={!!errors.primarySource}
              helperText={errors.primarySource}
            />
          </div>

          {/* Income Sources Section */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              What are the sources of income? (Select all that apply)
            </label>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.values(incomeSourceMap).map((label, index) => {
                // Group every 3 items for better layout
                if (label === "Others") {
                  return (
                    <div key={index} className="md:col-span-3">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={!!incomeSources[label]}
                          onChange={(e) =>
                            handleCheckboxChange(label, e.target.checked)
                          }
                          className="mr-2"
                          disabled={isDqaApproved || isDqaRejected}
                        />
                        <label className="text-sm font-medium text-gray-700">
                          {label}
                        </label>
                      </div>
                      {incomeSources["Others"] && (
                        <div className="mt-3 ml-6">
                          <input
                            type="text"
                            value={otherSource}
                            onChange={(e) => {
                              setOtherSource(e.target.value);
                              handleCheckboxChange("Others", e.target.value);
                            }}
                            className="bg-[#ebeef2] focus:bg-[#dde2e9] border border-[#cbd5e1] text-[#565656] font-[Regular] outline-none text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full px-3 py-2"
                            placeholder="Specify other income source"
                            disabled={isDqaApproved || isDqaRejected}
                          />
                          {Err("otherSource")}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={index} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={!!incomeSources[label]}
                      onChange={(e) =>
                        handleCheckboxChange(label, e.target.checked)
                      }
                      className="mr-2"
                      disabled={isDqaApproved || isDqaRejected}
                    />
                    <label className="text-sm font-medium text-gray-700">
                      {label}
                    </label>
                  </div>
                );
              })}
            </div>

            {Err("incomeSources")}
          </div>

          {/* Update Button - Only show when not DQA approved */}
          {!isDqaApproved && !isDqaRejected && (
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className={`${
                  loading ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
                } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          )}
        </form>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No income data available for this household
          </p>
        </div>
      )}
    </div>
  );
};

export default EditHouseholdIncome;